import type { CaseProgress } from './caseProgress'

/**
 * Roadmap #15 "case progress metrics" — a compact progress bar over a {@link CaseProgress} reading.
 * Token-driven only (no literal hex, CLAUDE.md §4): the fill uses `--iw-accent` (a neutral/accent
 * fill, never a reserved status color — status colors stay exclusive to `StatusPill`). Accessible:
 * a real `role="progressbar"` with `aria-valuenow`/`aria-valuemin`/`aria-valuemax` plus a text
 * alternative (`aria-label`, and — in the non-compact variant — a visible caption). `compact` is the
 * Cases-list cell (bar + a short "N%" caption, sized to fit a grid column without ever causing
 * page-level horizontal scroll); the default (non-compact) variant is the Case Overview placement,
 * which also spells out "Stage X of Y · label".
 */
export interface CaseProgressIndicatorProps {
  progress: CaseProgress
  compact?: boolean
}

export function CaseProgressIndicator({ progress, compact = false }: CaseProgressIndicatorProps) {
  const { doneCount, totalCount, percentComplete, currentStageLabel } = progress
  const accessibleLabel = `Case progress: stage ${doneCount} of ${totalCount}, ${percentComplete}% complete, currently ${currentStageLabel}`

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: compact ? 'row' : 'column',
        alignItems: compact ? 'center' : 'stretch',
        gap: compact ? 'var(--iw-space-2)' : 'var(--iw-space-1)',
        minWidth: compact ? 96 : 200,
        maxWidth: '100%',
      }}
    >
      <div
        role="progressbar"
        aria-valuenow={percentComplete}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={accessibleLabel}
        style={{
          position: 'relative',
          flex: compact ? '1 1 56px' : undefined,
          width: compact ? undefined : '100%',
          minWidth: compact ? 56 : undefined,
          height: compact ? 6 : 8,
          borderRadius: 'var(--iw-radius-sm)',
          background: 'var(--iw-neutral-200)',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: `${percentComplete}%`,
            background: 'var(--iw-accent)',
            borderRadius: 'var(--iw-radius-sm)',
          }}
        />
      </div>
      {compact ? (
        <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)', whiteSpace: 'nowrap' }}>
          {percentComplete}%
        </span>
      ) : (
        <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
          {`Stage ${doneCount} of ${totalCount} · ${percentComplete}% · ${currentStageLabel}`}
        </span>
      )}
    </div>
  )
}
